import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Product } from "@/data/products";
import { getPublicProductBySlug, listPublicProducts } from "@/lib/public.functions";
import { useProductsHidden } from "@/hooks/useSiteSettings";

interface ProductModalContextValue {
  currentProduct: Product | null;
  currentSlug: string | null;
  isLoading: boolean;
  isMissing: boolean;
  openProductModal: (slug: string) => void;
  closeProductModal: () => void;
}

const ProductModalContext = createContext<ProductModalContextValue | null>(null);

export function ProductModalProvider({ children }: { children: ReactNode }) {
  const productsHidden = useProductsHidden();
  const [slug, setSlug] = useState<string | null>(null);
  useEffect(() => { if (productsHidden) setSlug(null); }, [productsHidden]);

  // Try the already-cached list first (avoids a round trip when the modal
  // is opened from a card that came from listPublicProducts).
  const list = useQuery({
    queryKey: ["public-products"],
    queryFn: () => listPublicProducts(),
    staleTime: 60_000,
  });
  const fromList = slug ? list.data?.products.find((p) => p.slug === slug) ?? null : null;

  // Fallback: fetch the single product by slug (needed when the caller
  // — e.g. a recipe page — knows the slug but the list wasn't loaded).
  const single = useQuery({
    queryKey: ["public-product", slug],
    queryFn: () => getPublicProductBySlug({ slug: slug as string }),
    enabled: Boolean(slug) && !fromList,
    staleTime: 60_000,
  });

  const openProductModal = useCallback(
    (s: string) => { if (!productsHidden) setSlug(s); },
    [productsHidden],
  );
  const closeProductModal = useCallback(() => setSlug(null), []);

  const value = useMemo<ProductModalContextValue>(() => {
    const resolved = fromList ?? single.data?.product ?? null;
    const isLoading = Boolean(slug) && !fromList && single.isLoading;
    const isMissing = Boolean(slug) && !isLoading && !resolved;
    return {
      currentProduct: resolved,
      currentSlug: slug,
      isLoading,
      isMissing,
      openProductModal,
      closeProductModal,
    };
  }, [slug, fromList, single.data, single.isLoading, openProductModal, closeProductModal]);

  return <ProductModalContext.Provider value={value}>{children}</ProductModalContext.Provider>;
}

export function useProductModal() {
  const ctx = useContext(ProductModalContext);
  if (!ctx) throw new Error("useProductModal must be used inside <ProductModalProvider>");
  return ctx;
}
